import { uniqueId } from 'lodash';
import React, { useMemo, useRef, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { ExclamationCircleFill, FileEarmarkPdfFill, Upload } from 'react-bootstrap-icons';
import { toast } from 'sonner';
import { makeRESTApiRequest } from '../../../helpers/api';
import { ENDPOINTS, MAX_CHUNK_SIZE } from '../../../helpers/constants';
import './style.scss';

const DriveFileUploader = ({
  showTitle = true,
  uploadButtonText,
  onUploadedFilesChange,
  filesToBeUploaded = [],
  multiple = true,
  id,
  allowedFileTypes = [], // Example ['image/gif', 'image/jpeg', 'image/png']
  accept = '', // Only shows this type of file in file selection window
  size = 'sm',
  required = true,
  onFileSave,
  deletable = true,
  title = 'Upload Files To Drive',
  hideUploader,
  folderId,
  subFolderName,
  style = {},
  showUploadedFiles = true,
  variant = 'default',
  titleClass = 'largeFont'
}) => {
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInput = useRef(null);

  const onFileChange = async e => {
    let files = e.target.files ? [...e.target.files] : [];

    if (!files.length) return;

    if (files.some(f => f.size > MAX_CHUNK_SIZE)) {
      return toast.error(
        !multiple
          ? 'Maximum allowed file size is 512MB'
          : 'Some of the files you uploaded exceeds the allowed 512MB limit and are ignored'
      );
    }

    files = files.filter(f => f.size <= MAX_CHUNK_SIZE);

    if (multiple) {
      filesToBeUploaded.push(
        ...files.map(file => ({
          file,
          status: 'pending',
          id: uniqueId()
        }))
      );
    } else {
      filesToBeUploaded = files.map(file => ({
        file,
        status: 'pending',
        id: uniqueId()
      }));
    }

    onUploadedFilesChange([...filesToBeUploaded]);

    if (allowedFileTypes?.length) {
      const allowedFiles = Array.from(filesToBeUploaded)?.filter(file => allowedFileTypes.includes(file['type']));

      if (filesToBeUploaded?.length !== allowedFiles?.length) {
        if (multiple) {
          let fileDifference = filesToBeUploaded?.length - allowedFiles?.length;
          toast.error(
            `${fileDifference}/${filesToBeUploaded?.length} ${
              fileDifference < 2 ? 'File Is' : 'Files Are'
            } Not Suitable To Upload.`
          );
        } else {
          toast.error('This File Is Not Suitable To Upload.');
        }
        filesToBeUploaded = allowedFiles;
      }
    }

    await Promise.all(
      filesToBeUploaded
        .filter(file => file.status === 'pending')
        .map(async ({ file, id }) => {
          setUploadProgress(0);
          const formData = new FormData();
          formData.append('name', file.name);
          formData.append('mimeType', file.type);
          formData.append('folderId', folderId);
          // Only send when set — FormData stringifies undefined as "undefined"
          if (subFolderName) {
            formData.append('subFolderName', subFolderName);
          }
          formData.append('file', file);

          const currentlyUploadingFile = filesToBeUploaded.findIndex(f => f.id === id);

          const { response, error } = await makeRESTApiRequest({
            endpoint: ENDPOINTS.GOOGLE_DRIVE_UPLOAD,
            method: 'POST',
            contentType: 'multipart/form-data',
            requestBody: formData,
            stringify: false
          });

          if (error) {
            setUploadProgress(0);
            filesToBeUploaded[currentlyUploadingFile] = {
              ...filesToBeUploaded[currentlyUploadingFile],
              status: 'error'
            };
            onUploadedFilesChange([...filesToBeUploaded]);
            toast.error(error);
            return;
          } else {
            setUploadProgress(100);
            if (currentlyUploadingFile !== -1) {
              filesToBeUploaded[currentlyUploadingFile] = {
                ...filesToBeUploaded[currentlyUploadingFile],
                status: 'complete',
                fileId: response?.id || response?.fileId
              };
              onUploadedFilesChange([...filesToBeUploaded]);
              onFileSave && onFileSave(filesToBeUploaded[currentlyUploadingFile]);
            }
          }
        })
    );
  };

  const count = useMemo(() => filesToBeUploaded?.length, [filesToBeUploaded]);

  const removeFile = fileId => {
    onUploadedFilesChange([...filesToBeUploaded.filter(uploadedFile => uploadedFile?.id !== fileId)]);
  };

  const isDropzone = variant === 'upload-area';
  const uploadLabel =
    uploadButtonText || (filesToBeUploaded.length > 0 ? 'Upload More' : 'Upload');
  const showUploadTrigger =
    !filesToBeUploaded.some(fileToBeUploaded => fileToBeUploaded?.status === 'pending') &&
    !filesToBeUploaded.some(fileToBeUploaded =>
      !multiple ? fileToBeUploaded?.status === 'complete' : false
    );

  return (
    <>
      {showTitle && title && (
        <div className="d-flex justify-content-start align-items-center flex-wrap">
          <h6 className={`ml-1 my-2 font-weight-bold ${titleClass}`}>{title}</h6>
        </div>
      )}

      {!hideUploader && (
        <div
          className={isDropzone ? 'drive-file-uploader__dropzone' : 'text-center'}
          style={isDropzone ? undefined : style}
        >
          {count > 0 ? (
            <div className="py-1">
              <>
                {filesToBeUploaded.length && showUploadedFiles && (
                  <div className="mb-1">
                    {filesToBeUploaded.map(({ file, id, status }, index) => {
                      return (
                        <Button
                          key={index}
                          name={`${id}-file`}
                          data-name={file.name}
                          data-mimetype={file.mimetype}
                          data-value={file.data}
                          className="p-1 mr-1 mb-1 bg-primary-light text-dark"
                          variant="outline-primary"
                          onClick={e => (status === 'complete' && deletable ? removeFile(id) : e.stopPropagation())}
                          size={size}
                        >
                          <h6 className="tiny-text p-0 m-0 d-flex align-items-center justify-content-center">
                            <FileEarmarkPdfFill size={16} className="text-danger mr-1" />
                            {file?.name}{' '}
                            <span className="ml-2 d-flex align-items-center justify-content-center">
                              {status === 'pending' && (
                                <Spinner
                                  size="sm"
                                  variant="dark"
                                  animation="border"
                                  className="image-loading-spinner"
                                />
                              )}
                              {status === 'complete' && deletable && 'x'}
                              {status === 'error' && <ExclamationCircleFill className="text-danger" />}
                            </span>
                          </h6>
                        </Button>
                      );
                    })}
                  </div>
                )}
              </>
            </div>
          ) : null}
          {showUploadTrigger && (
            <>
              {isDropzone ? (
                <div
                  className="drive-file-uploader__trigger"
                  onClick={() => fileInput.current.click()}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      fileInput.current.click();
                    }
                  }}
                >
                  <span className="drive-file-uploader__trigger-icon">
                    <Upload size={16} />
                  </span>
                  <span className="drive-file-uploader__trigger-label">{uploadLabel}</span>
                </div>
              ) : (
                <Button
                  className="py-1 px-2 d-inline-flex align-items-center"
                  variant="outline-info"
                  onClick={() => fileInput.current.click()}
                  size={size}
                >
                  <Upload size={16} />
                  <h6 className="tiny-text p-0 m-0 ml-2">{uploadLabel}</h6>
                </Button>
              )}

              <input
                ref={fileInput}
                type="file"
                onChange={onFileChange}
                id={id}
                style={{ display: 'none' }}
                multiple={multiple}
                accept={accept}
                required={required}
              />
            </>
          )}
        </div>
      )}
    </>
  );
};

export default DriveFileUploader;
